import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { RefreshCw, Layout, Image, Type, Shuffle, Monitor, Edit2, Save, X, BarChart3, List, Maximize2, Plus, Minus } from 'lucide-react';
import { Slide, SlideElement, Theme, ThemeFont, SlideBorderStyle } from '../types';
import { SafeEChartsRenderer } from './SafeEChartsRenderer';
import { generateColorMosaic, generateColorBlur } from '../utils/canvasEffects';

// 화면 비율 정의
const ASPECT_RATIOS = {
  '16:9': { width: 960, height: 540, name: 'HD (16:9)' },
  '4:3': { width: 800, height: 600, name: '표준 (4:3)' },
  '1:1': { width: 600, height: 600, name: '정사각형 (1:1)' },
  '9:16': { width: 540, height: 960, name: '세로 (9:16)' },
  '21:9': { width: 840, height: 360, name: '울트라와이드 (21:9)' }
};

// 화면 구분 모드
const SCREEN_DIVISIONS = {
  none: { name: '없음', icon: '□', defaultSize: 0 },
  top: { name: '상단', icon: '▁', defaultSize: 25 },
  bottom: { name: '하단', icon: '▔', defaultSize: 25 },
  left: { name: '좌측', icon: '▏', defaultSize: 25 },
  right: { name: '우측', icon: '▕', defaultSize: 25 }
};

// 슬라이드 개수별 가능한 모든 레이아웃 생성
const generateDynamicLayouts = (count: number) => {
  const layouts = [];
  
  // 1단 레이아웃 (세로)
  layouts.push({
    name: `세로 ${count}단`,
    type: 'vertical-single',
    calculate: (containerSize: any, divisionInfo: any) => {
      const { availableWidth, availableHeight, startX, startY } = divisionInfo;
      const itemHeight = availableHeight / count;
      return Array.from({ length: count }, (_, i) => ({
        x: startX,
        y: startY + i * itemHeight,
        w: availableWidth,
        h: itemHeight
      }));
    }
  });
  
  // 1단 레이아웃 (가로)
  if (count > 1) {
    layouts.push({
      name: `가로 ${count}열`,
      type: 'horizontal-single',
      calculate: (containerSize: any, divisionInfo: any) => {
        const { availableWidth, availableHeight, startX, startY } = divisionInfo;
        const itemWidth = availableWidth / count;
        return Array.from({ length: count }, (_, i) => ({
          x: startX + i * itemWidth,
          y: startY,
          w: itemWidth,
          h: availableHeight
        }));
      }
    });
  }
  
  // 2단 레이아웃
  if (count >= 2) {
    // 가로 2단 분할 (중복 제거)
    const addedColumnLayouts = new Set();
    for (let left = 1; left < count; left++) {
      const right = count - left;
      // 비율이 같은 경우 작은 수가 앞에 오는 것만 추가
      const ratio = `${Math.min(left, right)}:${Math.max(left, right)}`;
      if (!addedColumnLayouts.has(ratio)) {
        addedColumnLayouts.add(ratio);
        layouts.push({
          name: `2열 (${left}:${right})`,
          type: 'two-column',
          calculate: (containerSize: any, divisionInfo: any) => {
            const { availableWidth, availableHeight, startX, startY } = divisionInfo;
            const colWidth = availableWidth / 2;
            const leftHeight = availableHeight / left;
            const rightHeight = availableHeight / right;
            const result = [];
            
            for (let i = 0; i < left; i++) {
              result.push({
                x: startX,
                y: startY + i * leftHeight,
                w: colWidth,
                h: leftHeight
              });
            }
            
            for (let i = 0; i < right; i++) {
              result.push({
                x: startX + colWidth,
                y: startY + i * rightHeight,
                w: colWidth,
                h: rightHeight
              });
            }
            
            return result;
          }
        });
      }
    }
    
    // 세로 2단 분할 (중복 제거)
    const addedRowLayouts = new Set();
    for (let top = 1; top < count; top++) {
      const bottom = count - top;
      // 비율이 같은 경우 작은 수가 앞에 오는 것만 추가
      const ratio = `${Math.min(top, bottom)}:${Math.max(top, bottom)}`;
      if (!addedRowLayouts.has(ratio)) {
        addedRowLayouts.add(ratio);
        layouts.push({
          name: `2행 (${top}:${bottom})`,
          type: 'two-row',
          calculate: (containerSize: any, divisionInfo: any) => {
            const { availableWidth, availableHeight, startX, startY } = divisionInfo;
            const rowHeight = availableHeight / 2;
            const topWidth = availableWidth / top;
            const bottomWidth = availableWidth / bottom;
            const result = [];
            
            for (let i = 0; i < top; i++) {
              result.push({
                x: startX + i * topWidth,
                y: startY,
                w: topWidth,
                h: rowHeight
              });
            }
            
            for (let i = 0; i < bottom; i++) {
              result.push({
                x: startX + i * bottomWidth,
                y: startY + rowHeight,
                w: bottomWidth,
                h: rowHeight
              });
            }
            
            return result;
          }
        });
      }
    }
  }
  
  // 3단 레이아웃 (3개 이상일 때)
  if (count >= 3 && count % 3 === 0) {
    layouts.push({
      name: `3열 균등`,
      type: 'three-column',
      calculate: (containerSize: any, divisionInfo: any) => {
        const { availableWidth, availableHeight, startX, startY } = divisionInfo;
        const colWidth = availableWidth / 3;
        const itemsPerCol = count / 3;
        const itemHeight = availableHeight / itemsPerCol;
        const result = [];
        
        for (let col = 0; col < 3; col++) {
          for (let row = 0; row < itemsPerCol; row++) {
            result.push({
              x: startX + col * colWidth,
              y: startY + row * itemHeight,
              w: colWidth,
              h: itemHeight
            });
          }
        }
        
        return result;
      }
    });
  }
  
  // 그리드 레이아웃
  if (count === 4) {
    layouts.push({
      name: '2x2 그리드',
      type: 'grid-2x2',
      calculate: (containerSize: any, divisionInfo: any) => {
        const { availableWidth, availableHeight, startX, startY } = divisionInfo;
        const itemWidth = availableWidth / 2;
        const itemHeight = availableHeight / 2;
        return [
          { x: startX, y: startY, w: itemWidth, h: itemHeight },
          { x: startX + itemWidth, y: startY, w: itemWidth, h: itemHeight },
          { x: startX, y: startY + itemHeight, w: itemWidth, h: itemHeight },
          { x: startX + itemWidth, y: startY + itemHeight, w: itemWidth, h: itemHeight }
        ];
      }
    });
  }
  
  if (count === 6) {
    layouts.push({
      name: '2x3 그리드',
      type: 'grid-2x3',
      calculate: (containerSize: any, divisionInfo: any) => {
        const { availableWidth, availableHeight, startX, startY } = divisionInfo;
        const itemWidth = availableWidth / 3;
        const itemHeight = availableHeight / 2;
        const result = [];
        for (let row = 0; row < 2; row++) {
          for (let col = 0; col < 3; col++) {
            result.push({
              x: startX + col * itemWidth,
              y: startY + row * itemHeight,
              w: itemWidth,
              h: itemHeight
            });
          }
        }
        return result;
      }
    });
    
    layouts.push({
      name: '3x2 그리드',
      type: 'grid-3x2',
      calculate: (containerSize: any, divisionInfo: any) => {
        const { availableWidth, availableHeight, startX, startY } = divisionInfo;
        const itemWidth = availableWidth / 2;
        const itemHeight = availableHeight / 3;
        const result = [];
        for (let row = 0; row < 3; row++) {
          for (let col = 0; col < 2; col++) {
            result.push({
              x: startX + col * itemWidth,
              y: startY + row * itemHeight,
              w: itemWidth,
              h: itemHeight
            });
          }
        }
        return result;
      }
    });
  }
  
  // 특수 레이아웃 (5개일 때)
  if (count === 5) {
    layouts.push({
      name: '2+3 혼합',
      type: 'mixed-2-3',
      calculate: (containerSize: any, divisionInfo: any) => {
        const { availableWidth, availableHeight, startX, startY } = divisionInfo;
        const rowHeight = availableHeight / 2;
        const topWidth = availableWidth / 2;
        const bottomWidth = availableWidth / 3;
        return [
          { x: startX, y: startY, w: topWidth, h: rowHeight },
          { x: startX + topWidth, y: startY, w: topWidth, h: rowHeight },
          { x: startX, y: startY + rowHeight, w: bottomWidth, h: rowHeight },
          { x: startX + bottomWidth, y: startY + rowHeight, w: bottomWidth, h: rowHeight },
          { x: startX + bottomWidth * 2, y: startY + rowHeight, w: bottomWidth, h: rowHeight }
        ];
      }
    });
    
    layouts.push({
      name: '3+2 혼합',
      type: 'mixed-3-2',
      calculate: (containerSize: any, divisionInfo: any) => {
        const { availableWidth, availableHeight, startX, startY } = divisionInfo;
        const rowHeight = availableHeight / 2;
        const topWidth = availableWidth / 3;
        const bottomWidth = availableWidth / 2;
        return [
          { x: startX, y: startY, w: topWidth, h: rowHeight },
          { x: startX + topWidth, y: startY, w: topWidth, h: rowHeight },
          { x: startX + topWidth * 2, y: startY, w: topWidth, h: rowHeight },
          { x: startX, y: startY + rowHeight, w: bottomWidth, h: rowHeight },
          { x: startX + bottomWidth, y: startY + rowHeight, w: bottomWidth, h: rowHeight }
        ];
      }
    });
    
    layouts.push({
      name: '1+4 특수',
      type: 'special-1-4',
      calculate: (containerSize: any, divisionInfo: any) => {
        const { availableWidth, availableHeight, startX, startY } = divisionInfo;
        const mainHeight = availableHeight * 0.6;
        const subHeight = availableHeight * 0.4;
        const subWidth = availableWidth / 4;
        return [
          { x: startX, y: startY, w: availableWidth, h: mainHeight },
          { x: startX, y: startY + mainHeight, w: subWidth, h: subHeight },
          { x: startX + subWidth, y: startY + mainHeight, w: subWidth, h: subHeight },
          { x: startX + subWidth * 2, y: startY + mainHeight, w: subWidth, h: subHeight },
          { x: startX + subWidth * 3, y: startY + mainHeight, w: subWidth, h: subHeight }
        ];
      }
    });
  }
  
  return layouts;
};

// 텍스트 생성
const generateRandomText = (length = 'medium') => {
  const lengths: { [key: string]: number } = {
    tiny: 30,
    short: 100,
    medium: 200,
    long: 400,
    veryLong: 600
  };
  
  const sentences = [
    '혁신적인 기술이 우리의 미래를 바꾸고 있습니다.',
    '사용자 경험을 최우선으로 생각합니다.',
    '지속 가능한 성장을 위한 전략적 접근.',
    '창의적인 솔루션으로 문제를 해결합니다.',
    '협업을 통한 시너지 효과를 극대화합니다.'
  ];
  
  const targetLength = lengths[length] || lengths.medium;
  let result = '';
  
  while (result.length < targetLength) {
    result += sentences[Math.floor(Math.random() * sentences.length)] + ' ';
  }
  
  return result.substring(0, targetLength);
};

// 차트 데이터 생성
const generateChartData = () => {
  const types = ['line', 'bar', 'pie'];
  const type = types[Math.floor(Math.random() * types.length)];
  
  if (type === 'pie') {
    return {
      type,
      data: [
        { name: 'A', value: Math.floor(Math.random() * 100) + 20 },
        { name: 'B', value: Math.floor(Math.random() * 100) + 20 },
        { name: 'C', value: Math.floor(Math.random() * 100) + 20 },
        { name: 'D', value: Math.floor(Math.random() * 100) + 20 }
      ]
    };
  }
  
  return {
    type,
    data: Array.from({ length: 6 }, (_, i) => ({
      name: `${i + 1}월`,
      value: Math.floor(Math.random() * 100) + 20,
      value2: Math.floor(Math.random() * 100) + 20
    }))
  };
};

// 목록 데이터 생성
const generateListData = () => {
  const types = ['bullet', 'numbered'];
  const type = types[Math.floor(Math.random() * types.length)];
  const items = [
    '프로젝트 계획 수립',
    '팀 미팅 진행',
    '고객 요구사항 분석',
    '품질 관리 프로세스',
    '신규 기능 개발'
  ];
  
  return {
    type,
    items: items.slice(0, Math.floor(Math.random() * 3) + 3).map(text => ({ text }))
  };
};

// 더미 이미지 URL
const getRandomImage = (id: number) => {
  const categories = ['nature', 'city', 'technology', 'abstract'];
  return `https://source.unsplash.com/400x300/?${categories[id % categories.length]}&sig=${id}`;
};

// 차트 컴포넌트 (ECharts 기반)
const ChartComponent = ({ data }: { data: any }) => {
  // ECharts 포맷으로 데이터 변환
  const convertToEChartsFormat = (data: any) => {
    if (data.type === 'pie') {
      return {
        type: 'pie',
        data: {
          labels: data.data.map((item: any) => item.name),
          values: data.data.map((item: any) => item.value),
        },
      };
    }
    
    if (data.type === 'line') {
      return {
        type: 'line',
        data: {
          labels: data.data.map((item: any) => item.name),
          values: data.data.map((item: any) => item.value),
        },
      };
    }
    
    if (data.type === 'bar') {
      return {
        type: 'bar',
        data: {
          labels: data.data.map((item: any) => item.name),
          values: data.data.map((item: any) => item.value),
        },
      };
    }
    
    return data;
  };

  const chartData = convertToEChartsFormat(data);

  return (
    <div className="w-full h-full">
      {chartData ? (
        <SafeEChartsRenderer
          chartData={chartData}
          theme={undefined}
          isDarkMode={false}
          width={300}
          height={200}
        />
      ) : (
        <div className="flex items-center justify-center text-gray-500 w-full h-full">
          No chart data
        </div>
      )}
    </div>
  );
};

// 목록 컴포넌트
const ListComponent = ({ data }: { data: any }) => {
  return (
    <div className="h-full overflow-auto p-2">
      {data.type === 'bullet' && (
        <ul className="space-y-1">
          {data.items.map((item: any, i: number) => (
            <li key={i} className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              <span className="text-sm">{item.text}</span>
            </li>
          ))}
        </ul>
      )}
      
      {data.type === 'numbered' && (
        <ol className="space-y-1">
          {data.items.map((item: any, i: number) => (
            <li key={i} className="flex items-start">
              <span className="text-blue-500 mr-2 font-bold">{i + 1}.</span>
              <span className="text-sm">{item.text}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

// 슬라이드 그리드 요소 Props
interface SlideGridElementsProps {
  slide: Slide;
  theme?: Theme;
  themeFont?: ThemeFont;
  borderStyle?: SlideBorderStyle;
  isEditable?: boolean;
  selectedElementId?: string;
  editingElementId?: string | null;
  onElementSelect?: (elementId: string) => void;
  onElementUpdate?: (elementId: string, updates: Partial<SlideElement>) => void;
  onElementDelete?: (elementId: string) => void;
  onEditingElementChange?: (elementId: string | null) => void;
  onLayoutChange?: (layout: any[]) => void;
  containerWidth?: number;
  containerHeight?: number;
  aspectRatio?: string;
  screenDivision?: string;
  divisionSize?: number;
  divisionImage?: string;
  selectedLayoutIndex?: number;
  onAspectRatioChange?: (ratio: string) => void;
  onScreenDivisionChange?: (division: string) => void;
  onDivisionSizeChange?: (size: number) => void;
  onSelectedLayoutChange?: (index: number) => void;
  scale?: number;
}

// 슬라이드 아이템 컴포넌트
const SlideItem = ({ 
  element, 
  style, 
  isEditable, 
  isSelected, 
  isEditing, 
  onUpdate, 
  onDelete,
  onSelect,
  onStartEdit,
  onStopEdit,
  theme,
  themeFont,
  scale = 1
}: {
  element: SlideElement;
  style: React.CSSProperties;
  isEditable: boolean;
  isSelected: boolean;
  isEditing: boolean;
  onUpdate: (id: string, updates: Partial<SlideElement>) => void;
  onDelete: (id: string) => void;
  onSelect: (id: string) => void;
  onStartEdit: (id: string) => void;
  onStopEdit: () => void;
  theme?: Theme;
  themeFont?: ThemeFont;
  scale?: number;
}) => {
  const [editContent, setEditContent] = useState(element.content);
  const [effectTextures, setEffectTextures] = useState<{[key: string]: string}>({});

  const getDynamicFontSize = () => {
    const width = parseInt(style.width as string);
    const height = parseInt(style.height as string);
    let baseFontSize = Math.min(width / 20, height / 10, 20);
    
    if (element.type === 'text' && element.content && element.content.length < 100) {
      baseFontSize = Math.min(width / 10, height / 5, 32);
    }
    
    return {
      title: Math.max(14, baseFontSize * 1.2 * scale),
      content: Math.max(12, baseFontSize * scale),
      small: Math.max(10, baseFontSize * 0.8 * scale)
    };
  };

  const fontSize = getDynamicFontSize();

  const handleSave = () => {
    onUpdate(element.id, { content: editContent });
    onStopEdit();
  };

  // Apply theme colors if needed
  const elementColor = element.color || theme?.text || '#333';
  const elementBgColor = element.backgroundColor || theme?.background || '#fff';

  const elementStyle: React.CSSProperties = {
    ...style,
    padding: element.padding || 12,
    margin: element.margin || 0,
    backgroundColor: element.hideBackground ? 'transparent' : elementBgColor,
    color: elementColor,
    fontSize: (element.fontSize || fontSize.content) * scale,
    fontFamily: element.fontFamily || themeFont?.fontFamily || 'inherit',
    fontWeight: element.fontWeight,
    textAlign: element.textAlign || 'left',
    borderRadius: element.borderRadius || 8,
    transform: `rotate(${element.rotation || 0}deg)`,
    opacity: element.backgroundOpacity !== undefined ? element.backgroundOpacity : 1,
    border: element.borderWidth 
      ? `${element.borderWidth}px ${element.borderStyle || 'solid'} ${element.borderColor || '#000'}` 
      : 'none',
    boxShadow: element.boxShadow || (isSelected ? '0 0 0 2px #3B82F6' : 'none'),
    cursor: isEditable ? 'move' : 'default',
    position: 'absolute',
    overflow: 'hidden',
  };

  return (
    <div
      className={`slide-element ${isSelected ? 'selected' : ''} bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow`}
      style={elementStyle}
      onClick={(e) => {
        e.stopPropagation();
        if (isEditable) {
          onSelect(element.id);
        }
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (isEditable && element.type === 'text') {
          onStartEdit(element.id);
        }
      }}
    >
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-gray-800 flex-1" style={{ fontSize: `${fontSize.title}px` }}>
            {element.title || `요소 ${element.id.slice(0, 8)}`}
          </h3>
          
          {isEditable && (
            <div className="flex gap-1">
              {isEditing ? (
                <>
                  <button onClick={handleSave} className="p-1 text-green-600 hover:bg-green-100 rounded">
                    <Save size={16} />
                  </button>
                  <button onClick={onStopEdit} className="p-1 text-red-600 hover:bg-red-100 rounded">
                    <X size={16} />
                  </button>
                </>
              ) : (
                <button onClick={() => onStartEdit(element.id)} className="p-1 text-gray-600 hover:bg-gray-100 rounded">
                  <Edit2 size={16} />
                </button>
              )}
            </div>
          )}
        </div>
        
        <div className="flex-1 overflow-hidden">
          {element.type === 'text' && (
            isEditing ? (
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full h-full resize-none border rounded p-2"
                style={{ fontSize: `${fontSize.content}px` }}
              />
            ) : (
              <p className="text-gray-600" style={{ fontSize: `${fontSize.content}px` }}>
                {element.content}
              </p>
            )
          )}
          
          {element.type === 'image' && (
            <img 
              src={element.content} 
              alt={element.title || "Image"} 
              className="w-full h-full object-cover rounded" 
              draggable={false}
            />
          )}
          
          {element.type === 'video' && (
            <video 
              src={element.content} 
              className="w-full h-full object-contain"
              controls
              preload="metadata"
              playsInline
              style={{ backgroundColor: 'transparent' }}
            />
          )}
          
          {element.type === 'chart' && element.chartData && (
            <SafeEChartsRenderer
              chartData={element.chartData}
              theme={theme}
              isDarkMode={false}
              width={Math.floor(element.width) || 300}
              height={Math.floor(element.height) || 200}
            />
          )}
          
          {element.type === 'shape' && (
            <div className="w-full h-full relative">
              {renderShape(element)}
            </div>
          )}
          
          {element.content && typeof element.content === 'object' && (element.content as any).type === 'list' && (
            <ListComponent data={element.content} />
          )}
        </div>
        
        <div className="flex items-center justify-between mt-2" style={{ fontSize: `${fontSize.small}px` }}>
          <span className="text-gray-400 flex items-center gap-1">
            {element.type === 'chart' && <BarChart3 size={12} />}
            {element.type === 'text' && <Type size={12} />}
            {element.type === 'image' && <Image size={12} />}
            {element.type}
          </span>
          {isEditable && isSelected && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(element.id);
              }}
              className="p-1 text-red-600 hover:bg-red-100 rounded"
              title="삭제"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// 도형 렌더링 함수
const renderShape = (element: SlideElement) => {
  const fillColor = element.backgroundColor || element.color || '#3B82F6';
  const borderColor = element.borderColor || '#1E40AF';
  
  const shapeStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    backgroundColor: fillColor,
    border: element.borderWidth ? `${element.borderWidth}px ${element.borderStyle || 'solid'} ${borderColor}` : 'none',
  };

  switch (element.content) {
    case 'circle':
      return <div style={{ ...shapeStyle, borderRadius: '50%' }} />;
    
    case 'triangle':
      return (
        <div style={{
          width: 0,
          height: 0,
          borderLeft: '50px solid transparent',
          borderRight: '50px solid transparent',
          borderBottom: `100px solid ${fillColor}`,
        }} />
      );
    
    case 'star':
      return (
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <polygon
            points="50,15 61,35 82,35 66,50 72,71 50,56 28,71 34,50 18,35 39,35"
            fill={fillColor}
            stroke={borderColor}
            strokeWidth={element.borderWidth || 0}
          />
        </svg>
      );
    
    default: // square/rectangle
      return <div style={shapeStyle} />;
  }
};

// 메인 컴포넌트
export const SlideGridElements: React.FC<SlideGridElementsProps> = ({
  slide,
  theme,
  themeFont,
  borderStyle,
  isEditable = false,
  selectedElementId,
  editingElementId,
  onElementSelect,
  onElementUpdate,
  onElementDelete,
  onEditingElementChange,
  onLayoutChange,
  containerWidth = 1200,
  containerHeight = 675,
  aspectRatio = '16:9',
  screenDivision = 'none',
  divisionSize = 25,
  divisionImage = '',
  selectedLayoutIndex = 0,
  onAspectRatioChange,
  onScreenDivisionChange,
  onDivisionSizeChange,
  onSelectedLayoutChange,
  scale = 1
}) => {
  const [effectTextures, setEffectTextures] = useState<{[key: string]: string}>({});
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 슬라이드 요소들
  const elements = slide.elements || [];
  
  // 가능한 레이아웃 목록
  const availableLayouts = useMemo(() => {
    return elements.length > 0 ? generateDynamicLayouts(elements.length) : [];
  }, [elements.length]);

  // 현재 화면 비율
  const currentAspect = ASPECT_RATIOS[aspectRatio as keyof typeof ASPECT_RATIOS];
  
  // 화면 구분 정보 계산
  const getDivisionInfo = () => {
    const divSize = screenDivision === 'none' ? 0 : divisionSize / 100;
    let availableWidth = currentAspect.width;
    let availableHeight = currentAspect.height;
    let startX = 0;
    let startY = 0;
    
    if (screenDivision === 'top') {
      startY = currentAspect.height * divSize;
      availableHeight = currentAspect.height * (1 - divSize);
    } else if (screenDivision === 'bottom') {
      availableHeight = currentAspect.height * (1 - divSize);
    } else if (screenDivision === 'left') {
      startX = currentAspect.width * divSize;
      availableWidth = currentAspect.width * (1 - divSize);
    } else if (screenDivision === 'right') {
      availableWidth = currentAspect.width * (1 - divSize);
    }
    
    return { availableWidth, availableHeight, startX, startY };
  };

  // 레이아웃 계산
  const layoutItems = useMemo(() => {
    if (elements.length === 0 || availableLayouts.length === 0) {return [];}
    
    const layout = availableLayouts[selectedLayoutIndex];
    if (!layout) {return [];}
    
    const divisionInfo = getDivisionInfo();
    const positions = layout.calculate(currentAspect, divisionInfo);
    
    return elements.map((element, index) => ({
      ...element,
      ...positions[index]
    }));
  }, [elements, selectedLayoutIndex, availableLayouts, currentAspect, screenDivision, divisionSize]);

  // 화면 구분 스타일
  const getDivisionStyle = () => {
    if (screenDivision === 'none') {return null;}
    
    const size = divisionSize / 100;
    const styles: { [key: string]: React.CSSProperties } = {
      top: {
        left: 0, top: 0, width: '100%', height: `${size * 100}%`
      },
      bottom: {
        left: 0, bottom: 0, width: '100%', height: `${size * 100}%`
      },
      left: {
        left: 0, top: 0, width: `${size * 100}%`, height: '100%'
      },
      right: {
        right: 0, top: 0, width: `${size * 100}%`, height: '100%'
      }
    };
    
    return {
      position: 'absolute' as const,
      ...styles[screenDivision],
      backgroundImage: divisionImage ? `url(${divisionImage})` : undefined,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundColor: theme?.secondary || '#f3f4f6'
    };
  };

  // Generate textures for pattern effects
  useEffect(() => {
    const generateTextures = async () => {
      const newTextures: {[key: string]: string} = {};
      
      for (const element of elements) {
        const textureKey = `${element.id}-${element.backgroundPattern}-${element.backgroundColor}`;
        
        if (element.type === 'shape' && element.backgroundPattern && !effectTextures[textureKey]) {
          try {
            let textureUrl = '';
            const color = element.backgroundColor || '#3B82F6';
            
            if (element.backgroundPattern === 'mosaic') {
              textureUrl = generateColorMosaic(color, element.width || 100, element.height || 100, 8);
            } else if (element.backgroundPattern === 'blur') {
              textureUrl = generateColorBlur(color, element.width || 100, element.height || 100, 5);
            }
            
            if (textureUrl) {
              newTextures[textureKey] = textureUrl;
            }
          } catch {
            // Texture generation failed
          }
        }
      }
      
      if (Object.keys(newTextures).length > 0) {
        setEffectTextures(prev => ({ ...prev, ...newTextures }));
      }
    };

    generateTextures();
  }, [elements, effectTextures]);

  return (
    <div 
      ref={containerRef}
      className="slide-grid-elements absolute inset-0 bg-gray-200 rounded-lg shadow-lg overflow-auto"
      style={{ padding: '20px' }}
    >
      <div 
        className="relative bg-white mx-auto rounded-lg shadow-lg overflow-hidden"
        style={{ 
          width: `${currentAspect.width}px`,
          height: `${currentAspect.height}px`
        }}
      >
        {/* 화면 구분 이미지 */}
        {screenDivision !== 'none' && (
          <div style={getDivisionStyle()} className="rounded-lg overflow-hidden opacity-90" />
        )}
        
        {/* 슬라이드 아이템들 */}
        {layoutItems.map((item) => (
          <SlideItem
            key={item.id}
            element={item}
            style={{
              left: `${item.x}px`,
              top: `${item.y}px`,
              width: `${item.w}px`,
              height: `${item.h}px`
            }}
            isEditable={isEditable}
            isSelected={selectedElementId === item.id}
            isEditing={editingElementId === item.id}
            onUpdate={onElementUpdate || (() => {})}
            onDelete={onElementDelete || (() => {})}
            onSelect={onElementSelect || (() => {})}
            onStartEdit={onEditingElementChange || (() => {})}
            onStopEdit={() => onEditingElementChange?.(null)}
            theme={theme}
            themeFont={themeFont}
            scale={scale}
          />
        ))}
      </div>
    </div>
  );
};